// Live's handles on the canvas (issue 560; live's engine `transformer/transformer.ts`,
// `transformer/snapping.ts` and its clips' `getVisibleHandles`, read for behaviour): the maths of
// a drag on a chosen item, pure, in live's pixels (live's frame, `LIVE_FRAME`). A body
// drag moves it, snapped to the frame's and the other items' edges and centres; a corner scales
// it from the opposite corner (a picture keeps its ratio, a caption scales uniformly); a text's
// right edge reflows it, and its corner also scales its letters; just outside a corner, a drag
// turns it about its centre.
import type { Drawn } from "./engine/pick";
import type { Clip, ClipPatch } from "./timeline";

/** A drawn item's box, in live's pixels: its centre, size and clockwise turn in degrees. */
export interface Place {
  cx: number;
  cy: number;
  width: number;
  height: number;
  angle: number;
}

/** A drawn box (the frame's pixels) in live's pixels: `unit` is the frame's pixels to one of live's. */
export function placeOf(drawn: Drawn, unit: number): Place {
  return {
    cx: drawn.cx / unit,
    cy: drawn.cy / unit,
    width: drawn.width / unit,
    height: drawn.height / unit,
    angle: drawn.angle,
  };
}

/** A box on the frame, upright (a turned item's bounds), in live's pixels. */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Live's handles: the corners and the edges' middles (the turn zones are outside the corners). */
export type Handle = "tl" | "tr" | "bl" | "br" | "ml" | "mr" | "mt" | "mb";
export const CORNER_HANDLES = ["tl", "tr", "bl", "br"] as const;
/** Live's handles round several chosen items (its transformer with no one clip): all eight. */
export const GROUP_HANDLES: readonly Handle[] = ["tl", "tr", "bl", "br", "ml", "mr", "mt", "mb"];

/** Which way from the box's centre a handle lies, across and down: -1, 0 (its middle) or 1. */
const DIRECTION: Record<Handle, readonly [number, number]> = {
  tl: [-1, -1],
  tr: [1, -1],
  bl: [-1, 1],
  br: [1, 1],
  ml: [-1, 0],
  mr: [1, 0],
  mt: [0, -1],
  mb: [0, 1],
};

/**
 * The handles an item shows, as live's clips list them: a picture and a caption their corners
 * (a caption scales uniformly, #560), a text its right edge and bottom-right corner (live's
 * bottom edge changes nothing once let go, so it is left out). Every kind turns.
 */
export function handlesOf(kind: "picture" | "text" | "caption"): readonly Handle[] {
  if (kind === "text") return ["mr", "br"];
  return CORNER_HANDLES;
}


/** Live's least size of a box, a pixel each way. */
export const MIN_SIZE = 1;

/** Live's snap distance for a move: 5 of the screen's pixels. */
export const SNAP_SCREEN_PX = 5;

/** Live's snap distance for a corner or an edge: 5 of the frame's own pixels, whatever the zoom
 * (its resize snap runs at scale 1). */
export const SNAP_RESIZE_PX = 5;

const radians = (degrees: number) => (degrees * Math.PI) / 180;

/** A vector turned `degrees` clockwise (the frame's y runs down). */
function turn(point: Point, degrees: number): Point {
  const a = radians(degrees);
  return {
    x: point.x * Math.cos(a) - point.y * Math.sin(a),
    y: point.x * Math.sin(a) + point.y * Math.cos(a),
  };
}

/** A handle's offset from the box's centre before the turn. */
function offsetOf(handle: Handle, width: number, height: number): Point {
  const [across, down] = DIRECTION[handle];
  return { x: (across * width) / 2, y: (down * height) / 2 };
}

/** A handle's place on the frame, its box turned. */
export function handlePoint(place: Place, handle: Handle): Point {
  const offset = turn(offsetOf(handle, place.width, place.height), place.angle);
  return { x: place.cx + offset.x, y: place.cy + offset.y };
}

/** The box moved by a drag's travel. */
export function moved(place: Place, dx: number, dy: number): Place {
  return { ...place, cx: place.cx + dx, cy: place.cy + dy };
}

/**
 * The box a corner or edge drag makes (live's `#proposeScaledRect`): the opposite corner (or the
 * opposite edge's middle, for an edge's handle) stays where it is, and the dragged one follows
 * the pointer along the box's own turned axes; an edge changes one side only. A picture keeps its
 * ratio on a corner: the larger of the two stretches wins. Neither side goes under `MIN_SIZE`.
 */
export function scaled(place: Place, handle: Handle, pointer: Point, keepRatio: boolean): Place {
  const [across, down] = DIRECTION[handle];
  // The pivot: the opposite corner, or the opposite edge's middle.
  const turnedPivot = turn({ x: (-across * place.width) / 2, y: (-down * place.height) / 2 }, place.angle);
  const pivot = { x: place.cx + turnedPivot.x, y: place.cy + turnedPivot.y };
  // The pointer in the box's own axes, from the pivot.
  const local = turn({ x: pointer.x - pivot.x, y: pointer.y - pivot.y }, -place.angle);
  let width = across === 0 ? place.width : Math.abs(local.x);
  let height = down === 0 ? place.height : Math.abs(local.y);
  if (keepRatio && across !== 0 && down !== 0 && place.width > 0 && place.height > 0) {
    const ratio = place.width / place.height;
    if (width / place.width > height / place.height) height = width / ratio;
    else width = height * ratio;
  }
  width = Math.max(MIN_SIZE, width);
  height = Math.max(MIN_SIZE, height);
  // The new centre: half the new size from the pivot, along the box's axes.
  const centre = turn({ x: (across * width) / 2, y: (down * height) / 2 }, place.angle);
  return { ...place, cx: pivot.x + centre.x, cy: pivot.y + centre.y, width, height };
}

/**
 * The turn a drag outside a corner makes (live's `#rotate`): the angle the pointer has swept
 * about the box's centre since the drag began, added to the box's own, in degrees from -180 to
 * 180. Live does not snap a turn.
 */
export function rotated(place: Place, from: Point, to: Point): number {
  const start = Math.atan2(from.y - place.cy, from.x - place.cx);
  const now = Math.atan2(to.y - place.cy, to.x - place.cx);
  const angle = place.angle + ((now - start) * 180) / Math.PI;
  return normalAngle(angle);
}

/** An angle as -180 (exclusive) to 180 degrees, rounded to a hundredth. */
export function normalAngle(degrees: number): number {
  let angle = degrees % 360;
  if (angle > 180) angle -= 360;
  if (angle <= -180) angle += 360;
  return Math.round(angle * 100) / 100;
}

/** A turned box's upright bounds. */
export function boundsOf(place: Place): Rect {
  const corners = CORNER_HANDLES.map((corner) => handlePoint(place, corner));
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

/** Several boxes' joint upright bounds. */
export function unionOf(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  const x = Math.min(...rects.map((rect) => rect.x));
  const y = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return { x, y, width: right - x, height: bottom - y };
}

/** Several items' joint box, upright: live's transformer round a selection of them. */
export function jointOf(places: readonly Place[]): Place | null {
  const union = unionOf(places.map(boundsOf));
  if (union === null) return null;
  const { x, y, width, height } = union;
  return { cx: x + width / 2, cy: y + height / 2, width, height, angle: 0 };
}

/** A snap's line, drawn while the drag holds it: across at `position`, from `start` to `end`. */
export interface Guide {
  type: "vertical" | "horizontal";
  position: number;
  start: number;
  end: number;
}

interface Target {
  value: number;
  from: number;
  to: number;
}

/**
 * Live's snap of a moving box (`SnappingManager.snapMove`): on each axis, the first target within
 * `threshold` of the box's start, centre or end takes it, the frame's centre first, then its
 * start and end, then each other item's start, centre and end; a guide marks each snap.
 */
export function snapMove(
  box: Rect,
  frame: { width: number; height: number },
  others: readonly Rect[],
  threshold: number,
): { dx: number; dy: number; guides: Guide[] } {
  const guides: Guide[] = [];
  const axis = (
    targets: readonly Target[],
    start: number,
    size: number,
    across: [number, number],
    type: Guide["type"],
  ): number => {
    const points = [start, start + size / 2, start + size];
    for (const target of targets) {
      for (const point of points) {
        const diff = target.value - point;
        if (Math.abs(diff) < threshold) {
          guides.push({
            type,
            position: target.value,
            start: Math.min(target.from, across[0]),
            end: Math.max(target.to, across[1]),
          });
          return diff;
        }
      }
    }
    return 0;
  };
  const targetsX: Target[] = [
    { value: frame.width / 2, from: 0, to: frame.height },
    { value: 0, from: 0, to: frame.height },
    { value: frame.width, from: 0, to: frame.height },
    ...others.flatMap((other) =>
      [other.x, other.x + other.width / 2, other.x + other.width].map((value) => ({
        value,
        from: other.y,
        to: other.y + other.height,
      })),
    ),
  ];
  const targetsY: Target[] = [
    { value: frame.height / 2, from: 0, to: frame.width },
    { value: 0, from: 0, to: frame.width },
    { value: frame.height, from: 0, to: frame.width },
    ...others.flatMap((other) =>
      [other.y, other.y + other.height / 2, other.y + other.height].map((value) => ({
        value,
        from: other.x,
        to: other.x + other.width,
      })),
    ),
  ];
  const dx = axis(targetsX, box.x, box.width, [box.y, box.y + box.height], "vertical");
  const dy = axis(targetsY, box.y, box.height, [box.x, box.x + box.width], "horizontal");
  return { dx, dy, guides };
}

/** What a drag does: a move, a turn, or a handle's scale. */
export type Gesture = "move" | "turn" | Handle;

/**
 * A chosen item's change from a drag, as the `setClip` patch its Parameters would make, from the
 * box it was drawn in when the drag began (`start`) to the drag's box now (`next`). A picture's
 * box and turn; a text's place, width, turn, and on its corner its letters' size (live scales the
 * size by the larger stretch); a caption's place, uniform scale and turn. The reducer holds
 * each value to its range.
 */
export function dragPatch(clip: Clip, gesture: Gesture, start: Place, next: Place): ClipPatch | null {
  const dx = next.cx - start.cx;
  const dy = next.cy - start.cy;
  switch (clip.kind) {
    case "video":
    case "image":
      if (gesture === "turn") return { rotation: next.angle };
      return {
        box: { x: next.cx - next.width / 2, y: next.cy - next.height / 2, width: next.width, height: next.height },
      };
    case "text": {
      const style = clip.style;
      if (gesture === "turn") return { style: { rotation: next.angle } };
      if (gesture === "move") return { style: { x: style.x + dx, y: style.y + dy } };
      // Live keeps a text's top left where it was and reflows it to the new width.
      const across = start.width > 0 ? next.width / start.width : 1;
      const down = start.height > 0 ? next.height / start.height : 1;
      const width = style.width * across;
      if (gesture !== "br") return { style: { width } };
      return { style: { width, size: Math.round(style.size * Math.max(across, down)) } };
    }
    case "caption": {
      // Its box carries its centre; a corner scales it uniformly about its own centre, which the
      // opposite corner's staying moves (live's caption keeps its own size, #560).
      if (gesture === "turn") return { rotation: next.angle };
      const box = { ...clip.box, x: clip.box.x + dx, y: clip.box.y + dy };
      if (gesture === "move") return { box };
      return { box, scale: clip.scale * (start.width > 0 ? next.width / start.width : 1) };
    }
    case "audio":
    case "effect":
      return null;
    default:
      return clip satisfies never;
  }
}

/** A drag under way: what it does, where each dragged item was drawn when it began, the pointer. */
export interface DragState {
  gesture: Gesture;
  starts: ReadonlyMap<string, Place>;
  from: Point;
  to: Point;
}

/**
 * Where a drag has taken its items, as each one's patch, and the snap guides it shows. A move
 * takes every dragged item by the pointer's travel, their joint bounds snapped (live's); a turn
 * or a handle acts on the one item, the handle following the pointer as it was grabbed.
 */
export function dragOutcome(
  drag: DragState,
  clips: readonly Clip[],
  others: readonly Rect[],
  frame: { width: number; height: number },
  threshold: number,
): { patches: { id: string; patch: ClipPatch }[]; guides: Guide[] } {
  const patches: { id: string; patch: ClipPatch }[] = [];
  const clipOf = (id: string) => clips.find((clip) => clip.id === id);
  if (drag.gesture === "move") {
    let dx = drag.to.x - drag.from.x;
    let dy = drag.to.y - drag.from.y;
    const joint = unionOf([...drag.starts.values()].map((start) => boundsOf(moved(start, dx, dy))));
    const snap = joint === null ? { dx: 0, dy: 0, guides: [] } : snapMove(joint, frame, others, threshold);
    dx += snap.dx;
    dy += snap.dy;
    for (const [id, start] of drag.starts) {
      const clip = clipOf(id);
      const patch = clip === undefined ? null : dragPatch(clip, "move", start, moved(start, dx, dy));
      if (patch !== null) patches.push({ id, patch });
    }
    return { patches, guides: snap.guides };
  }
  if (drag.starts.size > 1) return groupOutcome(drag, clips);
  const guides: Guide[] = [];
  for (const [id, start] of drag.starts) {
    const clip = clipOf(id);
    if (clip === undefined) continue;
    let next: Place;
    if (drag.gesture === "turn") {
      next = { ...start, angle: rotated(start, drag.from, drag.to) };
    } else {
      // The handle keeps the place the pointer took it by.
      const grabbed = handlePoint(start, drag.gesture);
      const pointer = { x: drag.to.x + grabbed.x - drag.from.x, y: drag.to.y + grabbed.y - drag.from.y };
      // A picture keeps its ratio, a caption scales uniformly (live's); a text reflows.
      next = scaled(start, drag.gesture, pointer, clip.kind !== "text");
      // Live snaps the moving edges of an upright box; a turned one's handle meets its targets
      // where it is drawn (`snapTurned`).
      if (start.angle === 0) {
        const snap = snapResize(boundsOf(next), drag.gesture, frame, others, SNAP_RESIZE_PX);
        next = { ...next, ...centreOf(snap.rect) };
        guides.push(...snap.guides);
      } else {
        const snap = snapTurned(start, next, drag.gesture, frame, others, SNAP_RESIZE_PX);
        next = snap.place;
        guides.push(...snap.guides);
      }
    }
    const patch = dragPatch(clip, drag.gesture, start, next);
    if (patch !== null) patches.push({ id, patch });
  }
  return { patches, guides };
}

/** An upright box's centre and size. */
function centreOf(rect: Rect): Pick<Place, "cx" | "cy" | "width" | "height"> {
  return { cx: rect.x + rect.width / 2, cy: rect.y + rect.height / 2, width: rect.width, height: rect.height };
}

/**
 * Live's snap of a corner or an edge (`SnappingManager.snapResize`): on each axis, the first
 * target within `threshold` of an edge the handle moves takes that edge, the opposite one
 * staying; the frame's start, centre and end come first, then each other item's start, centre
 * and end. Live keeps no ratio here, so a snapped picture may leave its ratio by up to the
 * reach, as live's does.
 */
export function snapResize(
  box: Rect,
  handle: Handle,
  frame: { width: number; height: number },
  others: readonly Rect[],
  threshold: number,
): { rect: Rect; guides: Guide[] } {
  const rect = { ...box };
  const guides: Guide[] = [];
  const [dirX, dirY] = DIRECTION[handle];
  const movingLeft = dirX === -1;
  const movingRight = dirX === 1;
  const movingTop = dirY === -1;
  const movingBottom = dirY === 1;
  const across = resizeTargets(frame.width, frame.height, others, (each) => [each.x, each.width, each.y, each.height]);
  for (const target of across) {
    const left = target.value - box.x;
    const right = target.value - (box.x + box.width);
    if (movingLeft && Math.abs(left) < threshold) {
      rect.x += left;
      rect.width -= left;
    } else if (movingRight && Math.abs(right) < threshold) {
      rect.width += right;
    } else {
      continue;
    }
    guides.push({
      type: "vertical",
      position: target.value,
      start: Math.min(target.from, box.y),
      end: Math.max(target.to, box.y + box.height),
    });
    break;
  }
  const down = resizeTargets(frame.height, frame.width, others, (each) => [each.y, each.height, each.x, each.width]);
  for (const target of down) {
    const top = target.value - box.y;
    const bottom = target.value - (box.y + box.height);
    if (movingTop && Math.abs(top) < threshold) {
      rect.y += top;
      rect.height -= top;
    } else if (movingBottom && Math.abs(bottom) < threshold) {
      rect.height += bottom;
    } else {
      continue;
    }
    guides.push({
      type: "horizontal",
      position: target.value,
      start: Math.min(target.from, box.x),
      end: Math.max(target.to, box.x + box.width),
    });
    break;
  }
  return { rect, guides };
}

/** The targets a handle snaps to on one axis, live's resize order: the frame's start, centre and
 * end, then each other item's; each with the span its guide runs across. */
function resizeTargets(
  size: number,
  across: number,
  others: readonly Rect[],
  other: (rect: Rect) => [number, number, number, number],
): Target[] {
  return [
    ...[0, size / 2, size].map((value) => ({ value, from: 0, to: across })),
    ...others.flatMap((each) => {
      const [start, extent, from, length] = other(each);
      return [start, start + extent / 2, start + extent].map((value) => ({ value, from, to: from + length }));
    }),
  ];
}

/**
 * A turned item's handle snap (ours, issue 560: live measures a turned box as if it were upright
 * from its turned top left corner, so its snaps land off their targets). The handle being
 * dragged, where it is drawn, meets the first target within `threshold` on each axis, the
 * frame's first, then the other items': a corner lands on the targets it reaches (at most one
 * across and one down), an edge's middle where its own turned axis crosses its target. The box
 * keeps its turn and its opposite corner or edge; a snapped corner keeps no ratio, as an upright
 * one's snap (live's).
 */
export function snapTurned(
  start: Place,
  next: Place,
  handle: Handle,
  frame: { width: number; height: number },
  others: readonly Rect[],
  threshold: number,
): { place: Place; guides: Guide[] } {
  const point = handlePoint(next, handle);
  const hitX = resizeTargets(frame.width, frame.height, others, (each) => [each.x, each.width, each.y, each.height]).find(
    (target) => Math.abs(target.value - point.x) < threshold,
  );
  const hitY = resizeTargets(frame.height, frame.width, others, (each) => [each.y, each.height, each.x, each.width]).find(
    (target) => Math.abs(target.value - point.y) < threshold,
  );
  if (hitX === undefined && hitY === undefined) return { place: next, guides: [] };
  const [across, down] = DIRECTION[handle];
  let to: Point;
  let snappedX = hitX;
  let snappedY = hitY;
  if (across !== 0 && down !== 0) {
    to = { x: hitX?.value ?? point.x, y: hitY?.value ?? point.y };
  } else {
    // An edge's middle moves along the box's own axis only: it meets a target where that axis
    // crosses it, across first; an axis nearly along a target's line never reaches it.
    const axis = turn({ x: across, y: down }, start.angle);
    if (hitX !== undefined && Math.abs(axis.x) > 0.2) {
      to = { x: hitX.value, y: point.y + ((hitX.value - point.x) / axis.x) * axis.y };
      snappedY = undefined;
    } else if (hitY !== undefined && Math.abs(axis.y) > 0.2) {
      to = { x: point.x + ((hitY.value - point.y) / axis.y) * axis.x, y: hitY.value };
      snappedX = undefined;
    } else {
      return { place: next, guides: [] };
    }
  }
  const place = scaled(start, handle, to, false);
  const bounds = boundsOf(place);
  const guides: Guide[] = [];
  if (snappedX !== undefined) {
    guides.push({
      type: "vertical",
      position: snappedX.value,
      start: Math.min(snappedX.from, bounds.y),
      end: Math.max(snappedX.to, bounds.y + bounds.height),
    });
  }
  if (snappedY !== undefined) {
    guides.push({
      type: "horizontal",
      position: snappedY.value,
      start: Math.min(snappedY.from, bounds.x),
      end: Math.max(snappedY.to, bounds.x + bounds.width),
    });
  }
  return { place, guides };
}

/**
 * A handle or a turn on several chosen items (live's transformer round them): the joint box
 * scales from its opposite corner or edge, free of any ratio (live keeps one only for a single
 * picture), or turns about its centre; each item follows, its centre carried and its own size
 * or turn changed with it. A turned item's sides lengthen by the stretch along them, as live's
 * stretch of its matrix makes them (`#deltaScale`), its turn kept. A text scales its letters and
 * width by the larger of those, about its carried centre, as live's, and a caption its scale.
 */
function groupOutcome(drag: DragState, clips: readonly Clip[]): { patches: { id: string; patch: ClipPatch }[]; guides: Guide[] } {
  const joint = jointOf([...drag.starts.values()]);
  const patches: { id: string; patch: ClipPatch }[] = [];
  if (joint === null || drag.gesture === "move") return { patches, guides: [] };
  let carry: (start: Place) => Place;
  if (drag.gesture === "turn") {
    const by = rotated(joint, drag.from, drag.to);
    carry = (start) => {
      const centre = turn({ x: start.cx - joint.cx, y: start.cy - joint.cy }, by);
      return { ...start, cx: joint.cx + centre.x, cy: joint.cy + centre.y, angle: normalAngle(start.angle + by) };
    };
  } else {
    const handle = drag.gesture;
    const grabbed = handlePoint(joint, handle);
    const pointer = { x: drag.to.x + grabbed.x - drag.from.x, y: drag.to.y + grabbed.y - drag.from.y };
    const next = scaled(joint, handle, pointer, false);
    const sx = joint.width > 0 ? next.width / joint.width : 1;
    const sy = joint.height > 0 ? next.height / joint.height : 1;
    const [across, down] = DIRECTION[handle];
    const pivot = { x: joint.cx - (across * joint.width) / 2, y: joint.cy - (down * joint.height) / 2 };
    carry = (start) => {
      // Its own axes, turned, under the stretch: each side's factor is its axis's new length.
      const a = radians(start.angle);
      return {
        ...start,
        cx: pivot.x + (start.cx - pivot.x) * sx,
        cy: pivot.y + (start.cy - pivot.y) * sy,
        width: start.width * Math.hypot(sx * Math.cos(a), sy * Math.sin(a)),
        height: start.height * Math.hypot(sx * Math.sin(a), sy * Math.cos(a)),
      };
    };
  }
  for (const [id, start] of drag.starts) {
    const clip = clips.find((each) => each.id === id);
    const patch = clip === undefined ? null : carriedPatch(clip, start, carry(start));
    if (patch !== null) patches.push({ id, patch });
  }
  return { patches, guides: [] };
}

/** An item's patch when a selection's joint box carries it from `start` to `next`. */
function carriedPatch(clip: Clip, start: Place, next: Place): ClipPatch | null {
  switch (clip.kind) {
    case "video":
    case "image":
      return {
        box: { x: next.cx - next.width / 2, y: next.cy - next.height / 2, width: next.width, height: next.height },
        rotation: next.angle,
      };
    case "text": {
      const style = clip.style;
      const k = Math.max(
        start.width > 0 ? next.width / start.width : 1,
        start.height > 0 ? next.height / start.height : 1,
      );
      // Its drawn box's top left, moved with the centre and grown by the larger stretch.
      const x = style.x + (next.cx - (start.width * k) / 2) - (start.cx - start.width / 2);
      const y = style.y + (next.cy - (start.height * k) / 2) - (start.cy - start.height / 2);
      return k === 1
        ? { style: { x, y, rotation: next.angle } }
        : { style: { x, y, rotation: next.angle, width: style.width * k, size: Math.round(style.size * k) } };
    }
    case "caption": {
      // Carried, turned with the joint box, and scaled uniformly by the larger of its own sides'
      // stretches, as live's caption's font is (#560).
      const k = Math.max(
        start.width > 0 ? next.width / start.width : 1,
        start.height > 0 ? next.height / start.height : 1,
      );
      return {
        box: { ...clip.box, x: clip.box.x + next.cx - start.cx, y: clip.box.y + next.cy - start.cy },
        rotation: next.angle,
        ...(k === 1 ? {} : { scale: clip.scale * k }),
      };
    }
    case "audio":
    case "effect":
      return null;
    default:
      return clip satisfies never;
  }
}
